import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";
import Textarea from "components/form/Textarea";
import RichTextEditor from "components/form/RichTextEditor";

const BASE_URL = "https://api.kennapartners.com/api/v1"; // Base URL for all API calls

const News = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [filteredNewsItems, setFilteredNewsItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isInitialLoadRef = useRef(true); // Track if this is initial load
  const isSearchingRef = useRef(false); // Track if we're currently searching
  const allNewsItemsCacheRef = useRef(null); // Cache all news items to avoid repeated API calls

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState("");
  const [newNewsId, setNewNewsId] = useState(null);

  // Current news item being operated on
  const [currentNews, setCurrentNews] = useState(null);
  const [selectedNewsId, setSelectedNewsId] = useState(null);

    /* ------------  PAGINATION  ---------------*/
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    date: new Date().toISOString().split("T")[0], // Default to today's date in YYYY-MM-DD format
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchNewsItems();          // <--- called every time currentPage changes
  }, [currentPage]); 

  // Initialize filteredNewsItems ONLY on initial load
  useEffect(() => {
    if (newsItems.length > 0 && isInitialLoadRef.current && !isSearchingRef.current) {
      applyFilters(newsItems);
      isInitialLoadRef.current = false; // Mark that initial load is done
    }
  }, [newsItems]);

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim()) {
      isSearchingRef.current = true;
      const debounceTimer = setTimeout(() => {
        searchAllNewsItems(searchTerm.trim());
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      // Search cleared - reset to current page with status filter applied
      isSearchingRef.current = false;
      applyFilters(newsItems);
    }
  }, [searchTerm]);

  // Status filter changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      applyFilters(newsItems);
    } else {
      // Re-apply search with new status filter
      searchAllNewsItems(searchTerm.trim());
    }
  }, [statusFilter]);

const fetchNewsItems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${BASE_URL}/news/?page=${currentPage}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      if (data?.data?.news && Array.isArray(data.data.news)) {
        setNewsItems(data.data.news);
        setFilteredNewsItems(data.data.news); // Initialize filtered news items
        setTotalItems(data.data.total_news || 0);
      } else {
        setNewsItems([]);
        setFilteredNewsItems([]);
        setTotalItems(0);
      }
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to fetch news items.");
      setNewsItems([]);
      setFilteredNewsItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const searchAllNewsItems = async (term) => {
    setLoading(true);
    try {
      let allNewsItems = allNewsItemsCacheRef.current;

      // Only fetch if we don't have cached data
      if (!allNewsItems) {
        const token = localStorage.getItem("access_token");
        const response = await fetch(
          `${BASE_URL}/news/?page=1&limit=10000`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        allNewsItems = data.data?.news || [];
        
        // Cache the results for future searches
        allNewsItemsCacheRef.current = allNewsItems;
      }

      // Filter from cache - this is instant!
      let filtered = allNewsItems.filter((newsItem) => {
        const titleMatch = newsItem?.title?.toLowerCase().startsWith(term.toLowerCase());
        return titleMatch; // ONLY return titles that START WITH the search term
      });

      // Apply status filter if needed
      if (statusFilter !== "all") {
        filtered = filtered.filter((newsItem) => newsItem.status === statusFilter);
      }

      setFilteredNewsItems(filtered);
    } catch (error) {
      console.error("Error searching news items:", error);
      setFilteredNewsItems([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (items) => {
    let filtered = Array.isArray(items) ? items : [];

    if (statusFilter !== "all") {
      filtered = filtered.filter((newsItem) => newsItem.status === statusFilter);
    }

    setFilteredNewsItems(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      date: new Date().toISOString().split("T")[0],
    });
    setFile(null);
  };

  // Create News Item
  const handleCreateNews = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");

      // 1. First create the news item without the file
      const response = await fetch(`${BASE_URL}/news/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: formData.title,
          content: {
            html: formData.content,
            text: formData.content.replace(/<[^>]*>/g, ""),
          },
          date: formData.date,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newNewsItem = data.data.news;

      // 2. If there's a file, upload it
      if (file) {
        const formDataToSend = new FormData();
        formDataToSend.append("file", file);

        const uploadResponse = await fetch(
          `${BASE_URL}/news/${newNewsItem.id}/upload`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formDataToSend,
          }
        );

        if (!uploadResponse.ok) {
          throw new Error(
            `File upload failed! status: ${uploadResponse.status}`
          );
        }

        const uploadData = await uploadResponse.json();
        newNewsItem.file_url =
          uploadData.data?.news?.file_url || uploadData.file_url;
      }

      // 3. Update state with the new item (including file_url if uploaded)
      setNewsItems((prev) => [...prev, newNewsItem]);
      setShowCreateModal(false);
      resetForm();
      setFile(null);
    } catch (error) {
      console.error("Error:", error);
      setError(
        error.message || "Failed to create news item. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Get Single News Item Details
const fetchNewsItemDetails = async (id) => {
  setLoading(true);
  try {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${BASE_URL}/news/${id}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const newsData = data.news || data.data?.news || data;

    if (!newsData) {
      throw new Error("No news data found in response");
    }

    // Ensure content is properly structured
    if (typeof newsData.content === 'string') {
      newsData.content = {
        html: newsData.content,
        text: newsData.content.replace(/<[^>]*>/g, "")
      };
    }

    setCurrentNews(newsData);
    setError("");
  } catch (error) {
    console.error("Error fetching news item details:", error);
    setError("Failed to fetch news item details.");
    setCurrentNews(null);
  } finally {
    setLoading(false);
  }
};

  // Update News Item
  const handleUpdateNews = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");

      // 1. First update the news item
      const response = await fetch(`${BASE_URL}/news/${currentNews.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: formData.title,
          content: {
            html: formData.content,
            text: formData.content.replace(/<[^>]*>/g, ""),
          },
          date: formData.date,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      let updatedNewsItem = await response.json();

      // 2. If there's a new file, upload it
      if (file) {
        const formDataToSend = new FormData();
        formDataToSend.append("file", file);

        const uploadResponse = await fetch(
          `${BASE_URL}/news/${currentNews.id}/upload`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formDataToSend,
          }
        );

        if (!uploadResponse.ok) {
          throw new Error(
            `File upload failed! status: ${uploadResponse.status}`
          );
        }

        const uploadData = await uploadResponse.json();
        updatedNewsItem.file_url =
          uploadData.data?.news?.file_url || uploadData.file_url;
      }

      // 3. Update state
      setNewsItems((prev) =>
        prev.map((item) =>
          item.id === currentNews.id ? updatedNewsItem : item
        )
      );
      setShowEditModal(false);
      setFile(null);
    } catch (error) {
      console.error("Error updating news item:", error);
      setError(
        error.message || "Failed to update news item. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete News Item
  const handleDeleteNews = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/news/${selectedNewsId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setNewsItems((prev) =>
        prev.filter((newsItem) => newsItem.id !== selectedNewsId)
      );
      setShowDeleteModal(false);
      setSelectedNewsId(null);
      setError("");
    } catch (error) {
      console.error("Error deleting news item:", error);
      setError("Failed to delete news item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Upload File
  const handleFileUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      const formDataToSend = new FormData();

      // Check if file exists
      if (!file) {
        throw new Error("Please select a file to upload");
      }

      formDataToSend.append("file", file);

      const response = await fetch(
        `${BASE_URL}/news/${selectedNewsId}/upload`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataToSend,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Upload failed with status ${response.status}`
        );
      }

      const updatedNewsItem = await response.json();

      // Update the news items state with the new file URL
      setNewsItems((prev) =>
        prev.map((item) =>
          item.id === selectedNewsId
            ? {
                ...item,
                file_url:
                  updatedNewsItem.data?.news?.file_url ||
                  updatedNewsItem.file_url,
              }
            : item
        )
      );

      setShowUploadModal(false);
      setFile(null);
    } catch (error) {
      console.error("Error uploading file:", error);
      setError(error.message || "Failed to upload file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

const openEditModal = (newsItem) => {
  setCurrentNews(newsItem);
  setFormData({
    title: newsItem.title || "",
    content: typeof newsItem.content === 'string' 
      ? newsItem.content 
      : newsItem.content?.html || newsItem.content || "",
    date: newsItem.date
      ? newsItem.date.split('T')[0]
      : new Date().toISOString().split('T')[0],
  });
  setShowEditModal(true);
};

  const openDeleteModal = (newsItemId) => {
    setSelectedNewsId(newsItemId);
    setShowDeleteModal(true);
  };

  const openDetailsModal = async (newsItemId) => {
    setSelectedNewsId(newsItemId);
    await fetchNewsItemDetails(newsItemId);
    setShowDetailsModal(true);
  };

  const openUploadModal = (newsItemId) => {
    setSelectedNewsId(newsItemId);
    setShowUploadModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setShowUploadModal(false);
    setCurrentNews(null);
    setSelectedNewsId(null);
    resetForm();
  };

    /* ------------------------------------------------------------------ */
  /* ----------  PAGINATION HELPERS  -----------------------------------*/
  /* ------------------------------------------------------------------ */
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-between items-center mt-4 px-4 pb-4">
        <div>
          <p className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> news items
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border"
          >
            Previous
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 border rounded-md ${
                currentPage === page
                  ? ""
                  : ""
              }`}
            >
              {page}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <main className="workspace">
      {fileSizeError && (
        <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",zIndex:9999,display:"flex",alignItems:"flex-start",gap:"12px",backgroundColor:"#dc2626",color:"white",fontSize:"14px",fontWeight:"500",padding:"16px 20px",borderRadius:"10px",boxShadow:"0 10px 40px rgba(0,0,0,0.35)",maxWidth:"500px",width:"calc(100% - 32px)"}}>
          <span style={{fontSize:"20px",lineHeight:"1",flexShrink:0}}>🚫</span>
          <div style={{flex:1,lineHeight:"1.5"}}>{fileSizeError}</div>
          <button onClick={()=>setFileSizeError("")} style={{marginLeft:"8px",background:"none",border:"none",color:"white",fontSize:"22px",lineHeight:"1",cursor:"pointer",flexShrink:0,fontWeight:"bold",padding:0}}>×</button>
        </div>
      )}
      <Breadcrumb title="News">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>News</BreadcrumbItem>
      </Breadcrumb>

      {error && (
        <div className="alert-danger mb-4">
          {error}
          <button className="float-right text-white" onClick={() => setError("")}>
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">News Management</h1>
        <div className="flex gap-2">
          <button onClick={openCreateModal} className="btn-primary">
            <span className="la la-plus"></span> Add New News
          </button>
          <button onClick={fetchNewsItems} className="btn-secondary" disabled={loading}>
            <span className="la la-refresh"></span> Refresh
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search news by title or content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-control"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading && !newsItems.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading news items...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover w-full">
                  <thead>
                    <tr>
                      <th className="w-2/5">Title</th>
                      <th className="w-1/4">Publication Date</th>
                      <th className="w-1/6">File URL</th>
                      <th className="w-1/5">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNewsItems.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8">
                          {newsItems.length === 0
                            ? "No news items found. Create your first news item!"
                            : "No news items match your search criteria."}
                        </td>
                      </tr>
                    ) : (
                      filteredNewsItems.map((item) => (
                        <tr key={item.id}>
                          <td className="w-2/5">
                            <div className="font-medium">{item.title}</div>
                          </td>
                          <td className="w-1/4">
                            {item.date
                              ? new Date(item.date).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "-"}
                          </td>
                          <td className="w-1/6">
                            {item.file_url ? (
                              <a
                                href={item.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:underline"
                              >
                                View File
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="w-1/5">
                            <div className="flex gap-1">
                              <Button
                                onClick={() => openDetailsModal(item.id)}
                                className="btn btn-sm btn-info"
                                title="View Details"
                              >
                                <span className="la la-eye"></span>
                              </Button>
                              <Button
                                onClick={() => openEditModal(item)}
                                className="btn btn-sm btn-warning"
                                title="Edit News"
                              >
                                <span className="la la-edit"></span>
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => openDeleteModal(item.id)}
                                className="btn btn-sm btn-danger"
                                title="Delete News"
                              >
                                <span className="la la-trash"></span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Create New News Item
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleCreateNews}>
                  <div className="modal-body p-6 space-y-6">
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Title *
                      </label>
                      <Input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    {/* Content Field */}
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Content *
                      </label>
                      <RichTextEditor
                        value={formData.content}
                        onChange={(html) =>
                          setFormData((prev) => ({ ...prev, content: html }))
                        }
                        uploadEndpoint={`${BASE_URL}/news/editor/upload`}
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Date *
                      </label>
                      <Input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Upload File
                      </label>
                      <input
                        type="file"
                        onChange={(e) => { const f=e.target.files[0]; if(!f) return; if(f.size > 2097152){ setFileSizeError(`⚠️ "${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please use a smaller file.`); e.target.value=""; return; } setFileSizeError(""); setFile(f); }}
                        className="block w-full text-sm text-gray-600
      file:mr-4 file:py-2.5 file:px-4
      file:rounded-md file:border-0
      file:text-sm file:font-medium
      file:bg-blue-50 file:text-blue-700
      hover:file:bg-blue-100"
                        accept=".png,.jpeg,.jpg"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Supported formats: .png, .jpeg, .jpg
                      </p>
                      {file && (
                        <p className="text-sm text-gray-600 mt-1">
                          Selected file: {file.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Edit News Item
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleUpdateNews}>
                  <div className="modal-body p-6 space-y-6">
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Title *
                      </label>
                      <Input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

{/* Content Field */}
<div className="form-group space-y-2">
  <label className="block text-sm font-medium text-gray-700">
    Content *
  </label>
  <RichTextEditor
    value={formData.content}
    onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
    uploadEndpoint={`${BASE_URL}/news/editor/upload`}
  />
</div>
                  </div>

                  <div className="form-group space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Date *
                    </label>
                    <Input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div className="form-group space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Upload File
                    </label>
                    <input
                      type="file"
                      onChange={(e) => { const f=e.target.files[0]; if(!f) return; if(f.size > 2097152){ setFileSizeError(`⚠️ "${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please use a smaller file.`); e.target.value=""; return; } setFileSizeError(""); setFile(f); }}
                      className="block w-full text-sm text-gray-600
      file:mr-4 file:py-2.5 file:px-4
      file:rounded-md file:border-0
      file:text-sm file:font-medium
      file:bg-blue-50 file:text-blue-700
      hover:file:bg-blue-100"
                      accept=".png,.jpeg,.jpg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Supported formats: .png, .jpeg, .jpg
                    </p>
                    {file && (
                      <p className="text-sm text-gray-600 mt-1">
                        Selected file: {file.name}
                      </p>
                    )}
                  </div>

                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b space-x-4">
                    <Button
                      type="button"
                      onClick={closeAllModals}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      Close
                    </Button>
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Updating...
                        </span>
                      ) : (
                        "Update News"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    onClick={closeAllModals}
                    className="btn-close text-red-500 dark:text-red-500 hover:text-red-700"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete this news item? This action
                    cannot be undone.
                  </p>
                </div>
                <div className="modal-footer flex justify-center gap-4">
                  <button
                    onClick={closeAllModals}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                  >
                    Cancel
                  </button>
                  <Button
                    color="danger"
                    onClick={handleDeleteNews}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Delete News"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-4xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-4 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold">
                    News Item Details
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <div className="modal-body p-6">
                  {loading ? (
                    <div className="text-center py-4">
                      <span className="la la-spinner la-spin text-2xl"></span>
                      <p>Loading details...</p>
                    </div>
                  ) : currentNews ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Basic Info Section */}
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Basic Information
                          </h3>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Title
                              </label>
                              <p className="mt-1 text-sm text-gray-900 font-semibold">
                                {currentNews.title || "Not specified"}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Publication Date
                              </label>
                              <p className="mt-1 text-sm text-gray-900">
                                {currentNews.date
                                  ? new Date(
                                      currentNews.date
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "Not specified"}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Created At
                              </label>
                              <p className="mt-1 text-sm text-gray-900">
                                {currentNews.created_at
                                  ? new Date(
                                      currentNews.created_at
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "Not specified"}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Last Updated
                              </label>
                              <p className="mt-1 text-sm text-gray-900">
                                {currentNews.updated_at
                                  ? new Date(
                                      currentNews.updated_at
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "Not specified"}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* File Info Section */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            File Information
                          </h3>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">
                              File URL
                            </label>
                            <div className="mt-1">
                              {currentNews.file_url ? (
                                <a
                                  href={currentNews.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-sm break-all"
                                >
                                  {currentNews.file_url}
                                </a>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  No file uploaded
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content Sections */}
 
<div className="space-y-4">
  <div className="bg-gray-50 p-4 rounded-lg">
    <h3 className="text-lg font-medium text-gray-900 mb-4">Content</h3>
    <div 
      className="prose max-w-none overflow-y-auto max-h-[50vh]"
      dangerouslySetInnerHTML={{ 
        __html: currentNews.content?.html || currentNews.content || "No content available." 
      }}
    />
  </div>
</div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-red-500 mb-4">
                        Failed to load news item details.
                      </p>
                      <button
                        onClick={() => fetchNewsItemDetails(selectedNewsId)}
                        className="btn bg-blue-500 text-white px-4 py-2 rounded"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
                <div className="modal-footer flex justify-end p-4 border-t gap-4">
                  <Button
                    onClick={closeAllModals}
                    className="btn bg-gray-300 text-gray-700 hover:bg-gray-400 px-6"
                  >
                    Close
                  </Button>
                  {currentNews && (
                    <Button
                      onClick={() => {
                        closeAllModals();
                        openEditModal(currentNews);
                      }}
                      className="btn bg-blue-600 text-white hover:bg-blue-700 px-6"
                    >
                      Edit News
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      <Footer />
    </main>
  );
};

export default News;